import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useCoverageType } from '../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import { ProviderLocation } from '../../../models/ProviderDetails';
import { getDistance } from '../../../utils/card.utils';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import {
  getAcceptingNewPatientsStatus,
  shortenZipCode,
  showTier2ProviderTagOnHeader,
} from '../../../utils/providerDetails.utils';
import { capitalizeFirstLetters } from '../../../utils/providerNames.utils';
import { getCurrentMember } from '../../../utils/user.utils';
import { Constants, ReverseDentalCodes } from '../../Constants';
import { FeatureFlags } from '../../ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { phoneOnly } from '../../ConstantsStyles';
import {
  BenefitsBadge,
  BenefitsBadgeType,
} from '../../DetailsPageHeader/BenefitsBadge';
import {
  LocationCardContainer,
  LocationCardStack,
  SelectLocationBadge,
  SelectLocationButton,
} from '../../SelectLocationCard/SelectLocationCard.styled';
import { getTierProviderEligibility } from '../../Utils/memberUtils/memberUtils';
import {
  AcceptingNewPatientsBadge,
  LocationBadgeRow,
  LocationCardWrapper,
  LocationDetailsAddress,
  LocationDetailsStack,
  MilesButton,
  PreferredBadgeWrapper,
  UpdateButtonMobileViewContainer,
} from './MapViewSelectLocationCard.style';

export type Props = {
  className?: string;
  location: ProviderLocation | FacilityLocationsResponse;
  selectLocation: () => void;
  highlightRefs: MutableRefObject<any>;
  handleDirectionClick: () => void;
  selected?: boolean;
  showBadgeRow?: boolean;
  showRadioSelect?: boolean;
  index?: number;
  totalLocations?: number;
  isDisabled?: boolean;
};

export const MapViewSelectLocationCard = ({
  location,
  showBadgeRow,
  selected,
  selectLocation,
  highlightRefs,
  handleDirectionClick,
  className = '',
  index = 0,
  totalLocations = 0,
  isDisabled = false,
}: Props) => {
  const phoneScreen = useMediaQuery(phoneOnly);
  const { t } = useTranslation();
  const {
    acceptingNewPatients,
    address,
    distance,
    isGreenDiamondProvider,
    isTieredProvider,
    isTier2Provider,
    locationId,
    providerGroupsAffiliations,
  } = location;
  const { city, line, state, postalCode, locationName } = address;
  const addressFontSizeMobile = phoneScreen ? 'sm' : 'lg';

  const [includeProviderGroupName] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.INCLUDE_PROVIDER_GROUP_NAME,
  ]);

  const coverageType = useCoverageType();

  const dentalCoverage = ReverseDentalCodes.includes(coverageType);

  const currentMember = getCurrentMember();
  const isCns = currentMember?.isCnSMember;

  const [showTier1Badge] = useFeatureFlag([
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ].TIER_ONE,
  ]);

  const [showTier2Badge] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_TWO_PROVIDER,
  ]);
  const [isDentalLocationNameEnable] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_DENTAL_LOCATION_NAME,
  ]);

  const displayCityOrState = includeProviderGroupName ? city : state;
  const displayLocationName =
    dentalCoverage && isDentalLocationNameEnable ? locationName : '';
  const addressString = `${line.join(
    ', '
  )},${displayCityOrState}, ${shortenZipCode(postalCode)}`;

  const displayTier1Badge =
    showTier1Badge &&
    getTierProviderEligibility(currentMember, isTieredProvider);

  const displayTier2Badge = showTier2ProviderTagOnHeader(
    showTier2Badge,
    currentMember?.isIFPTieredPlan,
    isTier2Provider
  );

  const { icon, acceptingPatientInfo, iconColor } =
    getAcceptingNewPatientsStatus(acceptingNewPatients);

  const isGehaTheme = shouldUseGehaV2Styling();

  return (
    <LocationCardContainer
      data-testid="select-location-container"
      onClick={() => {
        handleDirectionClick();
      }}
    >
      <LocationCardStack>
        <LocationDetailsStack
          alignItems="left"
          className="location-details"
          data-testid="location-details-stack"
          grow="true"
        >
          <div>
            <Text
              color="$core.color.neutral.90"
              fontSize={phoneScreen ? 'sm' : 'md'}
              fontWeight="medium"
            >
              {t('CHOOSE_PCP_FLOW.LOCATION_NUMBER', {
                index: index + 1,
                total: totalLocations,
              })}
            </Text>
          </div>
          <LocationCardWrapper
            aria-pressed={selected ? 'true' : 'false'}
            className={`location-card-wrap ${className}`}
            data-auto-testid="select-location-card"
            data-testid="select-location-card"
            style={
              !phoneScreen
                ? {
                    width: '100%',
                  }
                : {}
            }
          >
            {isGreenDiamondProvider && !isCns ? (
              <PreferredBadgeWrapper>
                <BenefitsBadge
                  badgeType={BenefitsBadgeType.GREEN_DIAMOND}
                  showInfoIcon={false}
                />
              </PreferredBadgeWrapper>
            ) : null}
            {!isGreenDiamondProvider && !isCns && displayTier1Badge ? (
              <PreferredBadgeWrapper>
                <BenefitsBadge
                  badgeType={BenefitsBadgeType.TIER_1}
                  showInfoIcon={false}
                />
              </PreferredBadgeWrapper>
            ) : null}
            {!isGreenDiamondProvider &&
            !displayTier1Badge &&
            displayTier2Badge ? (
              <PreferredBadgeWrapper>
                <BenefitsBadge
                  badgeType={BenefitsBadgeType.TIER_2}
                  showInfoIcon={false}
                />
              </PreferredBadgeWrapper>
            ) : null}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {includeProviderGroupName && providerGroupsAffiliations?.[0] && (
                <LocationDetailsAddress
                  color={isGehaTheme ? '$Primary1' : '$core.color.neutral.90'}
                  data-testid="location-provider-group"
                  fontSize={phoneScreen ? 'md' : 'lg'}
                  fontWeight="bold"
                >
                  {capitalizeFirstLetters(providerGroupsAffiliations[0]?.value)}
                </LocationDetailsAddress>
              )}
            </div>
            <div>
              <div>
                {displayLocationName && (
                  <Text
                    color="black"
                    data-testid="display-location-name"
                    fontSize={addressFontSizeMobile}
                    fontWeight="medium"
                  >
                    {displayLocationName}
                  </Text>
                )}
              </div>
              <div>
                <Text
                  color={isGehaTheme ? '$Primary1' : '$core.color.neutral.90'}
                  data-testid="address-string"
                  fontSize={addressFontSizeMobile}
                  fontWeight="medium"
                >
                  {addressString}
                </Text>
              </div>
            </div>
          </LocationCardWrapper>
          {showBadgeRow ? (
            <LocationBadgeRow
              data-auto-testid="select-location-badge-row"
              data-testid="select-location-badge-row"
              direction="row"
              justify="space-between"
            >
              <MilesButton
                data-auto-testid="select-location-miles-button"
                data-testid="select-location-miles-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDirectionClick();
                }}
                ref={(el: HTMLButtonElement) => {
                  if (highlightRefs) {
                    highlightRefs.current[locationId] = el;
                  }
                }}
              >
                <IconSymbol icon="directions" size="$sm" />
                <Text
                  color="$core.color.brand.60"
                  css={{ 'abyss-text-root': { fontSize: '12.64px' } }}
                  fontWeight="bold"
                >
                  {getDistance(distance)} {t('miles')}
                </Text>
              </MilesButton>
              <AcceptingNewPatientsBadge>
                <IconSymbol color={iconColor} icon={icon as any} size="$sm" />
                <Text
                  color={isGehaTheme ? '$Primary1' : '$core.color.neutral.90'}
                  fontSize={phoneScreen ? 'sm' : 'md'}
                  fontWeight="medium"
                >
                  {t(acceptingPatientInfo)}
                </Text>
              </AcceptingNewPatientsBadge>
            </LocationBadgeRow>
          ) : null}
          <UpdateButtonMobileViewContainer
            css={{
              marginTop: providerGroupsAffiliations?.[0] ? '0px' : '28px',
            }}
          ></UpdateButtonMobileViewContainer>
        </LocationDetailsStack>
        {selected ? (
          <SelectLocationBadge icon="check_circle" variant="success">
            {t('CHOOSE_PCP_FLOW.SELECTED_LOCATION')}
          </SelectLocationBadge>
        ) : (
          <SelectLocationButton
            data-testid="choose-location-button"
            isDisabled={isDisabled}
            onClick={() => {
              selectLocation();
              // handleDirectionClick();
            }}
            variant="outline"
          >
            {t('CHOOSE_PCP_FLOW.CHOOSE_THIS_LOCATION')}
          </SelectLocationButton>
        )}
      </LocationCardStack>
    </LocationCardContainer>
  );
};
